import { isValidPhoneNumber } from 'libphonenumber-js';
import { z } from 'zod';

import type { FormFieldConfig, FormWidgetConfig } from './form-widget.types';
import { isAgeWithin, parseIsoDate } from './date-field';
import { isFieldVisible } from './form-visibility';
import { getIdentificationConfig, validateIdentification } from './identification';
import { passesPasswordRules } from './password-rules';

const DEFAULT_MESSAGES = {
  required: 'Completa este campo.',
  email: 'Ingresa un correo electrónico válido.',
  phone: 'Ingresa un teléfono válido.',
  select: 'Selecciona una opción.',
  checkbox: 'Debes aceptar para continuar.',
  url: 'Ingresa un enlace válido (https://…).',
  urlOrEmail: 'Escribe un enlace (adipa.cl/empleos) o un correo (contacto@empresa.cl).',
  password: 'La contraseña todavía no cumple todos los requisitos.',
  passwordMatch: 'Las contraseñas no coinciden. Escribe la misma en ambos campos.',
  date: 'Elige una fecha válida.',
  file: 'Adjunta un archivo.',
  fileType: 'El archivo no tiene un formato permitido.',
  fileSize: 'El archivo pesa más de lo permitido.',
  signature: 'Firma para confirmar que aceptas las bases.',
};

// Formato indulgente: se acepta con o sin https://, y un correo a secas. La app
// normaliza después; el usuario no tiene que adivinar el formato.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;
const URL_PATTERN = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(:\d+)?(\/\S*)?$/i;

export function isUrlOrEmail(value: string): boolean {
  const trimmed = value.trim();
  return EMAIL_PATTERN.test(trimmed) || URL_PATTERN.test(trimmed);
}

function applyLengthRules(schema: z.ZodString, field: FormFieldConfig) {
  let next = schema;
  if (field.minLength) {
    next = next.min(field.minLength, `Escribe al menos ${field.minLength} caracteres.`);
  }
  if (field.maxLength) {
    next = next.max(field.maxLength, `Máximo ${field.maxLength} caracteres.`);
  }
  return next;
}

function getRequiredMessage(config: FormWidgetConfig, field: FormFieldConfig) {
  return (
    field.requiredMessage ??
    config.messages.validation?.required ??
    field.infoText ??
    DEFAULT_MESSAGES.required
  );
}

type CreateFormSchemaOptions = {
  relaxedIdentificationFieldNames?: Set<string>;
};

function matchesAccept(file: File, accept: string) {
  return accept.split(',').some((rule) => {
    const type = rule.trim().toLowerCase();
    if (!type) return false;
    if (type.startsWith('.')) return file.name.toLowerCase().endsWith(type);
    if (type.endsWith('/*')) return file.type.toLowerCase().startsWith(type.slice(0, -1));
    return file.type.toLowerCase() === type;
  });
}

function getMissingFileMessage(field: FormFieldConfig) {
  if (field.requiredMessage) return field.requiredMessage;
  return field.type === 'signature' ? DEFAULT_MESSAGES.signature : DEFAULT_MESSAGES.file;
}

function createFileSchema(config: FormWidgetConfig, field: FormFieldConfig) {
  const schema = z
    .custom<File>((value) => typeof File !== 'undefined' && value instanceof File, {
      message: getMissingFileMessage(field),
    })
    .refine((file) => !field.file?.accept || matchesAccept(file, field.file.accept), {
      message: field.file?.typeMessage ?? DEFAULT_MESSAGES.fileType,
    })
    .refine((file) => !field.file?.maxBytes || file.size <= field.file.maxBytes, {
      message: field.file?.sizeMessage ?? DEFAULT_MESSAGES.fileSize,
    });
  return field.required ? schema : schema.optional().or(z.literal(''));
}

function createFieldSchema(config: FormWidgetConfig, field: FormFieldConfig) {
  if (field.type === 'file' || field.type === 'signature') {
    return createFileSchema(config, field);
  }

  if (field.type === 'checkbox') {
    const schema = z.boolean();
    return field.required
      ? schema.refine(
          (value) => value,
          config.messages.validation?.checkbox ?? DEFAULT_MESSAGES.checkbox,
        )
      : schema.optional();
  }

  if (field.type === 'email') {
    const schema = z
      .string()
      .min(1, getRequiredMessage(config, field))
      .email(config.messages.validation?.email ?? DEFAULT_MESSAGES.email);
    return field.required ? schema : schema.optional().or(z.literal(''));
  }

  if (field.type === 'phone') {
    const schema = z.string().refine(
      (value) => {
        if (!value) return false;
        try {
          return isValidPhoneNumber(value);
        } catch {
          return false;
        }
      },
      { message: config.messages.validation?.phone ?? DEFAULT_MESSAGES.phone },
    );
    return field.required ? schema : schema.optional().or(z.literal(''));
  }

  if (field.type === 'url') {
    const schema = z
      .string()
      .min(1, getRequiredMessage(config, field))
      .url(config.messages.validation?.url ?? DEFAULT_MESSAGES.url);
    return field.required ? schema : schema.optional().or(z.literal(''));
  }

  if (field.type === 'url-or-email') {
    const schema = z
      .string()
      .min(1, getRequiredMessage(config, field))
      .refine(isUrlOrEmail, {
        message: config.messages.validation?.urlOrEmail ?? DEFAULT_MESSAGES.urlOrEmail,
      });
    return field.required ? schema : schema.optional().or(z.literal(''));
  }

  if (field.type === 'password') {
    const base = z.string().min(1, getRequiredMessage(config, field));
    const schema = field.password?.rules
      ? base.refine(passesPasswordRules, {
          message: config.messages.validation?.password ?? DEFAULT_MESSAGES.password,
        })
      : base;
    return field.required ? schema : schema.optional().or(z.literal(''));
  }

  if (field.type === 'date') {
    const schema = z
      .string()
      .min(1, getRequiredMessage(config, field))
      .refine((value) => parseIsoDate(value) !== undefined, {
        message: config.messages.validation?.date ?? DEFAULT_MESSAGES.date,
      })
      .refine((value) => !value || !field.date || isAgeWithin(value, field.date), {
        message:
          field.date?.message ?? config.messages.validation?.date ?? DEFAULT_MESSAGES.date,
      });
    return field.required ? schema : schema.optional().or(z.literal(''));
  }

  if (field.type === 'select') {
    const schema = z
      .string()
      .min(1, field.requiredMessage ?? config.messages.validation?.select ?? DEFAULT_MESSAGES.select);
    return field.required ? schema : schema.optional().or(z.literal(''));
  }

  const schema = applyLengthRules(
    z.string().min(1, getRequiredMessage(config, field)),
    field,
  );
  return field.required ? schema : schema.optional().or(z.literal(''));
}

export function createFormSchema(config: FormWidgetConfig, options: CreateFormSchemaOptions = {}) {
  const shape: Record<string, z.ZodTypeAny> = {};

  config.fields.forEach((field) => {
    shape[field.name] =
      field.visibleWhen || field.optionsWhen
        ? z.union([z.string(), z.boolean()]).optional()
        : createFieldSchema(config, field);
  });

  if (config.privacy.enabled) {
    shape.privacyAccepted = config.privacy.required
      ? z
          .boolean()
          .refine(
            (value) => value,
            config.messages.validation?.checkbox ?? DEFAULT_MESSAGES.checkbox,
          )
      : z.boolean().optional();
  }

  return z.object(shape).superRefine((values, ctx) => {
    config.fields.forEach((field) => {
      if (!isFieldVisible(field, values)) return;

      if (field.visibleWhen || field.optionsWhen) {
        const result = createFieldSchema(config, field).safeParse(values[field.name]);

        if (!result.success)
          result.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: [field.name],
            });
          });
      }

      const value = values[field.name];
      const matchField = field.type === 'password' ? field.password?.matchField : undefined;
      if (matchField && typeof value === 'string' && value !== values[matchField]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field.name],
          message: config.messages.validation?.passwordMatch ?? DEFAULT_MESSAGES.passwordMatch,
        });
      }

      if (
        field.validation?.type !== 'identification' ||
        options.relaxedIdentificationFieldNames?.has(field.name) ||
        typeof value !== 'string' ||
        value.length === 0
      ) {
        return;
      }

      const country = values[field.validation.countryField];
      if (validateIdentification(country, value)) return;

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [field.name],
        message: getIdentificationConfig(country).validationMessage,
      });
    });
  });
}
